import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Dumbbell, Target, Loader2 } from "lucide-react";
import { EXERCISES } from "@/data/exercises";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CustomExercise {
  name: string;
  category: string;
  muscle_groups: string[];
  equipment?: string;
  description?: string;
  instructions?: string;
}

interface ExerciseInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseName: string;
}

export const ExerciseInfoModal = ({ isOpen, onClose, exerciseName }: ExerciseInfoModalProps) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [customExercise, setCustomExercise] = useState<CustomExercise | null>(null);
  
  // Find the exercise from the built-in database
  const builtInExercise = EXERCISES.find(ex => 
    ex.name.toLowerCase() === exerciseName.toLowerCase()
  );

  // Helper to normalize names for matching
  const normalizeName = (str: string) =>
    str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const levenshteinDistance = (a: string, b: string) => {
    const matrix: number[][] = [];
    const lenA = a.length;
    const lenB = b.length;

    for (let i = 0; i <= lenB; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= lenA; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= lenB; i++) {
      for (let j = 1; j <= lenA; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    return matrix[lenB][lenA];
  };

  // Load exercise image from Supabase Storage
  useEffect(() => {
    const loadExerciseImage = async () => {
      if (!exerciseName || !isOpen) return;

      setIsLoadingImage(true);
      setImageUrl("");

      const rawNormalized = exerciseName.toLowerCase().replace(/\s+/g, "-");
      const normalized = normalizeName(exerciseName);
      const possibleExtensions = ["png", "jpg", "jpeg", "webp"];

      // Create variations of the filename to try directly
      const fileVariations = [
        rawNormalized, // original whitespace->hyphen version e.g., "hack-squat-machine"
        rawNormalized.split("-")[0], // first word only
        rawNormalized.split("-").slice(0, 2).join("-"), // first two words
        exerciseName.toLowerCase().replace(/\s+/g, ""), // no separators
      ].filter(Boolean);

      let imageFound = false;

      // First: try direct filename guesses
      for (const variation of fileVariations) {
        if (imageFound) break;

        for (const ext of possibleExtensions) {
          const filename = `${variation}.${ext}`;

          const { data } = supabase.storage
            .from("exercise-images")
            .getPublicUrl(filename);

          if (data?.publicUrl) {
            await new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => {
                setImageUrl(data.publicUrl);
                setIsLoadingImage(false);
                imageFound = true;
                resolve();
              };
              img.onerror = () => {
                resolve();
              };
              img.src = data.publicUrl;
            });

            if (imageFound) break;
          }
        }
      }

      // Second: fuzzy match against all stored filenames if still not found
      if (!imageFound) {
        try {
          const { data: files, error } = await supabase.storage
            .from("exercise-images")
            .list();

          if (!error && files && files.length > 0) {
            const candidates = files.map((f) => ({
              name: f.name,
              base: f.name.replace(/\.(png|jpg|jpeg|webp)$/i, ""),
            }));

            let bestMatch: { name: string; score: number } | null = null;

            for (const c of candidates) {
              const normImg = normalizeName(c.base);
              let score = levenshteinDistance(normalized, normImg);

              // Small bonus if the first word matches
              const firstWord = normalized.split("-")[0];
              if (normImg.includes(firstWord)) {
                score -= 1;
              }

              if (!bestMatch || score < bestMatch.score) {
                bestMatch = { name: c.name, score };
              }
            }

            if (bestMatch && bestMatch.score <= 3) {
              const { data } = supabase.storage
                .from("exercise-images")
                .getPublicUrl(bestMatch.name);

              if (data?.publicUrl) {
                await new Promise<void>((resolve) => {
                  const img = new Image();
                  img.onload = () => {
                    setImageUrl(data.publicUrl);
                    resolve();
                  };
                  img.onerror = () => {
                    resolve();
                  };
                  img.src = data.publicUrl;
                });
              }
            }
          }
        } catch (e) {
          console.error("Error fuzzy-matching exercise image", e);
        }

        setIsLoadingImage(false);
      }
    };

    loadExerciseImage();
  }, [exerciseName, isOpen]);

  // Load custom exercise if not found in built-in exercises
  useEffect(() => {
    const loadCustomExercise = async () => {
      if (!builtInExercise && isOpen && exerciseName) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const { data, error } = await supabase
            .from('custom_exercises')
            .select('*')
            .eq('user_id', user.id)
            .ilike('name', exerciseName)
            .maybeSingle();

          if (error) throw error;
          if (data) {
            setCustomExercise(data);
          }
        } catch (error) {
          console.error('Error loading custom exercise:', error);
        }
      }
    };

    loadCustomExercise();
  }, [isOpen, exerciseName, builtInExercise]);


  // Combine built-in and custom exercise data
  const exercise = builtInExercise || (customExercise ? {
    ...customExercise,
    id: 'custom',
  } : null);

  // Generate basic instructions based on category and exercise type
  const generateInstructions = () => {
    if (!exercise) {
      return "Exercise information not available.";
    }

    const name = exercise.name.toLowerCase();
    const category = exercise.category.toLowerCase();

    // Generate contextual instructions based on exercise characteristics
    if (category === 'cardio' || name.includes('running') || name.includes('cycling')) {
      return `1. Start with a proper warm-up\n2. Maintain steady breathing throughout\n3. Keep a consistent pace\n4. Monitor your heart rate\n5. Cool down gradually after finishing`;
    }

    if (name.includes('bench press')) {
      return `1. Lie flat on the bench with feet firmly planted on the floor\n2. Grip the bar slightly wider than shoulder-width\n3. Lower the bar to your chest in a controlled manner\n4. Press the bar up explosively while keeping your shoulder blades retracted\n5. Maintain a slight arch in your lower back throughout`;
    }

    if (name.includes('squat')) {
      return `1. Stand with feet shoulder-width apart\n2. Keep chest up and core engaged\n3. Lower down by pushing hips back and bending knees\n4. Go down until thighs are parallel to ground\n5. Push through heels to return to starting position`;
    }

    if (name.includes('deadlift')) {
      return `1. Stand with feet hip-width apart, bar over mid-foot\n2. Bend at hips and knees to grip the bar\n3. Keep back straight and chest up\n4. Drive through heels and extend hips to lift\n5. Lower the bar with control by pushing hips back`;
    }

    if (name.includes('pull-up') || name.includes('chin-up')) {
      return `1. Hang from the bar with arms fully extended\n2. Engage your core and shoulder blades\n3. Pull yourself up by driving elbows down\n4. Get chin over the bar\n5. Lower down with control to full extension`;
    }

    if (name.includes('row')) {
      return `1. Maintain a neutral spine throughout the movement\n2. Pull the weight towards your body, leading with your elbows\n3. Squeeze your shoulder blades together at the top\n4. Control the weight back to the starting position\n5. Keep core engaged to prevent lower back strain`;
    }

    if (name.includes('press') && category.includes('shoulder')) {
      return `1. Start with weight at shoulder level\n2. Keep core tight and avoid arching your back\n3. Press weight straight overhead\n4. Fully extend arms at the top\n5. Lower weight with control back to shoulders`;
    }

    if (name.includes('curl')) {
      return `1. Keep elbows close to your sides\n2. Curl weight up using only your biceps\n3. Don't swing or use momentum\n4. Squeeze at the top of the movement\n5. Lower weight slowly and with control`;
    }

    if (name.includes('lunge')) {
      return `1. Stand with feet hip-width apart\n2. Step forward with one leg\n3. Lower hips until both knees are bent at 90 degrees\n4. Push back through front heel to starting position\n5. Alternate legs or complete all reps on one side first`;
    }

    // Default instructions for other exercises
    return `1. Start in the proper position with good form\n2. Engage the target muscle groups\n3. Perform the movement with control\n4. Focus on full range of motion\n5. Breathe properly throughout the exercise`;
  };

  if (!exercise) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Dumbbell className="h-6 w-6 text-primary" />
              {exerciseName}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Exercise Demonstration Image */}
            <div 
              className="flex items-center justify-center bg-muted rounded-lg overflow-hidden relative"
              style={{ 
                aspectRatio: '1/1', 
                maxHeight: '500px',
                boxShadow: imageUrl ? '0 0 20px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.3), inset 0 0 60px rgba(16, 185, 129, 0.08)' : 'none',
                border: imageUrl ? '1px solid rgba(16, 185, 129, 0.4)' : 'none'
              }}
            >
              {isLoadingImage ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="text-sm text-muted-foreground">Loading image...</p>
                </div>
              ) : imageUrl ? (
                <img
                  src={imageUrl}
                  alt={`${exerciseName} demonstration`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 p-4 text-center">
                  <Dumbbell className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No image available for this exercise</p>
                </div>
              )}
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Detailed information not available for this exercise. This may be a custom exercise you created or an exercise from your workout program.
              </p>
            </div>

            <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
              <h3 className="text-sm font-semibold text-primary mb-2">💡 General Exercise Tips</h3>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Always warm up before performing exercises</li>
                <li>• Focus on proper form over heavy weight</li>
                <li>• Breathe consistently throughout the movement</li>
                <li>• Stop if you feel any sharp pain</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Dumbbell className="h-6 w-6 text-primary" />
            {exercise.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Exercise Demonstration Image */}
          <div 
            className="flex items-center justify-center bg-muted rounded-lg overflow-hidden relative"
            style={{ 
              aspectRatio: '1/1', 
              maxHeight: '500px',
              boxShadow: imageUrl ? '0 0 20px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.3), inset 0 0 60px rgba(16, 185, 129, 0.08)' : 'none',
              border: imageUrl ? '1px solid rgba(16, 185, 129, 0.4)' : 'none'
            }}
          >
            {isLoadingImage ? (
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">Loading image...</p>
              </div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={`${exerciseName} demonstration`}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 p-4 text-center">
                <Dumbbell className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No image available for this exercise</p>
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Category</h3>
            <Badge variant="secondary" className="text-sm">
              {exercise.category}
            </Badge>
          </div>

          {/* Equipment */}
          {exercise.equipment && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Equipment Needed</h3>
              <Badge variant="outline" className="text-sm">
                {exercise.equipment}
              </Badge>
            </div>
          )}

          {/* Muscle Groups */}
          {exercise.muscle_groups && exercise.muscle_groups.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <Target className="h-4 w-4" />
                Target Muscle Groups
              </h3>
              <div className="flex flex-wrap gap-2">
                {exercise.muscle_groups.map((group, index) => (
                  <Badge 
                    key={index} 
                    className="text-sm px-3 py-1 bg-primary/10 text-primary border-primary/20"
                  >
                    {group}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">How to Perform</h3>
            <div className="bg-background/50 rounded-lg p-4 border border-border/50">
              <div className="space-y-2 text-sm leading-relaxed whitespace-pre-line">
                {generateInstructions()}
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
            <h3 className="text-sm font-semibold text-primary mb-2">💡 Pro Tips</h3>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>• Always warm up before performing this exercise</li>
              <li>• Focus on proper form over heavy weight</li>
              <li>• Breathe consistently throughout the movement</li>
              <li>• Stop if you feel any sharp pain</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
